import { useEffect, useRef, useState } from "react";
import { Bell, X, CheckCircle2, FileCheck2, AlertTriangle, Receipt } from "lucide-react";
import api from "@/lib/api";
import { brl } from "@/lib/format";
import { toast } from "sonner";

/**
 * NotificacoesBell — sino filtrado por tipo.
 *
 * Props:
 *  - filter: "boleto" (default) → só mostra `tipo=boleto_pago` e toca o MP3 do boleto.
 *            "nota"              → só mostra `tipo=nfe_reconciled` e toca o WAV do chime.
 *
 * Cada instância mantém seu próprio dropdown, contagem de não-lidas e som.
 * Ambas pollam o mesmo `/api/notificacoes` a cada 20s.
 */
export default function NotificacoesBell({ filter = "boleto" }) {
  const isNota = filter === "nota";

  const [aberto, setAberto] = useState(false);
  const [itens, setItens] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const ref = useRef(null);
  const audioRef = useRef(null);
  // IDs de notificações já vistas por essa instância
  const idsVistosRef = useRef(new Set());
  // Primeira carga: só popula o set — não toca som p/ notificações que já existiam
  const primeiraCargaRef = useRef(true);
  const montadoEmRef = useRef(Date.now());
  const pendenteSomRef = useRef(false);

  // Lazy-init do <audio> — Chromium só permite play() após 1ª interação do usuário.
  // BOLETOS: usa arquivo MP3 clássico (som mais longo/grave, ~3.8s).
  // NOTAS: usa Web Audio API (chime sintético inline) — evita problemas de
  // codec/autoplay que ocorriam com o WAV externo. Chime dura ~280ms,
  // volume alto e distinto do MP3 do boleto.
  const audioContextRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  const getAudioContext = () => {
    if (!audioContextRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioContextRef.current = new AC();
    }
    return audioContextRef.current;
  };

  // Desbloqueio de audio no primeiro gesture do usuário (click/tap/keydown).
  // Chromium exige que AudioContext seja criado/resumido DENTRO de um handler
  // de user gesture — caso contrário fica em "suspended" para sempre.
  useEffect(() => {
    const unlock = () => {
      if (pendenteSomRef.current) { pendenteSomRef.current = false; setTimeout(() => tocarNotificacao(), 50); }
      if (audioUnlockedRef.current) return;
      try {
        const ctx = getAudioContext();
        if (ctx) {
          if (ctx.state === "suspended") ctx.resume();
          // Toca um som "vazio" (0 gain) para efetivamente destravar o pipeline
          const osc = ctx.createOscillator();
          const g = ctx.createGain();
          g.gain.value = 0;
          osc.connect(g); g.connect(ctx.destination);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.01);
          audioUnlockedRef.current = true;
        }
      } catch (e) {
        // silencioso — se falhar cai no fallback MP3
      }
    };
    // captura na fase de captura para pegar QUALQUER clique da página
    document.addEventListener("click", unlock, { capture: true, once: false });
    document.addEventListener("touchstart", unlock, { capture: true, once: false });
    document.addEventListener("keydown", unlock, { capture: true, once: false });
    return () => {
      document.removeEventListener("click", unlock, { capture: true });
      document.removeEventListener("touchstart", unlock, { capture: true });
      document.removeEventListener("keydown", unlock, { capture: true });
    };
  }, []);

  const getAudio = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio("/sounds/boleto_pago.mp3");
      audioRef.current.preload = "auto";
      audioRef.current.volume = 0.85;
    }
    return audioRef.current;
  };

  // Chime sintético via Web Audio API — duas notas ascendentes (A5 → C#6).
  // Bypass total de codec/autoplay: gerado inline pelo browser.
  const tocarChimeNota = () => {
    try {
      const ctx = getAudioContext();
      if (!ctx) return false;
      // Se o context nunca foi desbloqueado por gesture, retorna false para
      // cair no fallback MP3. Se estiver suspenso mas já unlocked, resume.
      if (ctx.state === "suspended") {
        ctx.resume();
        if (ctx.state === "suspended" && !audioUnlockedRef.current) return false;
      }
      const now = ctx.currentTime;
      const gain = 0.4;  // volume relativo — subiu de 0.35 p/ 0.4
      // Nota 1 — A5 (880Hz), 100ms
      const osc1 = ctx.createOscillator();
      const g1 = ctx.createGain();
      osc1.type = "sine";
      osc1.frequency.value = 880;
      osc1.connect(g1); g1.connect(ctx.destination);
      g1.gain.setValueAtTime(0, now);
      g1.gain.linearRampToValueAtTime(gain, now + 0.015);
      g1.gain.setValueAtTime(gain, now + 0.085);
      g1.gain.linearRampToValueAtTime(0, now + 0.10);
      osc1.start(now);
      osc1.stop(now + 0.11);
      // Nota 2 — C#6 (1108.73Hz), 150ms, com gap de 30ms
      const t2 = now + 0.13;
      const osc2 = ctx.createOscillator();
      const g2 = ctx.createGain();
      osc2.type = "sine";
      osc2.frequency.value = 1108.73;
      osc2.connect(g2); g2.connect(ctx.destination);
      g2.gain.setValueAtTime(0, t2);
      g2.gain.linearRampToValueAtTime(gain, t2 + 0.015);
      g2.gain.setValueAtTime(gain, t2 + 0.135);
      g2.gain.linearRampToValueAtTime(0, t2 + 0.15);
      osc2.start(t2);
      osc2.stop(t2 + 0.16);
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[nota-sino] chime falhou:", e);
      return false;
    }
  };

  const tocarNotificacao = () => {
    if (isNota) {
      const ok = tocarChimeNota();
      if (ok) return;
      // Fallback: toca o MP3 do boleto (sempre funciona) caso Web Audio falhe
    }
    try {
      const a = getAudio();
      a.currentTime = 0;
      const p = a.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Navegador bloqueou o MP3 (sem interação ainda): tenta o chime; se também bloqueado, toca no próximo clique
          if (!tocarChimeNota()) pendenteSomRef.current = true;
        });
      }
    } catch { pendenteSomRef.current = true; }
  };

  // Filtra somente notificações pertencentes a essa instância
  const filtrarTipo = (n) => (isNota ? (n.tipo === "nfe_reconciled" || n.tipo === "nota_autorizada") : n.tipo === "boleto_pago");

  // Dedup entre abas/recarregamentos: cada evento toca 1 vez por navegador
  const jaTocou = (id) => {
    try {
      const k = "df_beeps";
      const lst = JSON.parse(localStorage.getItem(k) || "[]");
      if (lst.includes(id)) return true;
      localStorage.setItem(k, JSON.stringify([...lst, id].slice(-300)));
    } catch {}
    return false;
  };

  const carregar = () => api.get("/notificacoes")
    .then((r) => {
      const itensBrutos = r.data.itens || [];
      const itensFiltrados = itensBrutos.filter(filtrarTipo);

      // Novo evento = id ainda não visto (mesmo que outro usuário já tenha marcado como lido)
      const novas = itensFiltrados.filter((n) => !idsVistosRef.current.has(n.id)
        && new Date(n.created_at).getTime() >= montadoEmRef.current - 120000);
      const paraTocar = primeiraCargaRef.current ? [] : novas.filter((n) => !jaTocou(n.id));

      if (paraTocar.length > 0) {
        tocarNotificacao();
        if (isNota) {
          disparaToastsNotas(paraTocar);
          try { window.dispatchEvent(new CustomEvent("nfe-reconciled", { detail: { count: paraTocar.length, itens: paraTocar } })); } catch {}
        } else {
          disparaToastsBoletos(paraTocar);
          try { window.dispatchEvent(new CustomEvent("boleto-pago", { detail: { count: paraTocar.length, itens: paraTocar } })); } catch {}
        }
      }
      if (primeiraCargaRef.current) novas.forEach((n) => jaTocou(n.id));

      itensFiltrados.forEach((n) => idsVistosRef.current.add(n.id));
      const naoLidasCount = itensFiltrados.filter((n) => !n.lida).length;
      setItens(itensFiltrados);
      setNaoLidas(naoLidasCount);
      primeiraCargaRef.current = false;
    })
    .catch(() => {});

  // Toast de boletos pagos (banco Inter/Asaas) — sempre individual, top-right (default do Toaster)
  const disparaToastsBoletos = (novos) => {
    novos.forEach((n) => {
      toast.success(n.mensagem || `Pagamento recebido — ${n.cliente_nome || "Cliente"}`, {
        id: `boleto-pago-${n.id}`,
        description: n.valor ? `Valor: ${brl(n.valor)}${n.provider ? ` • via ${n.provider.toUpperCase()}` : ""}` : "",
        duration: 5000,
        action: {
          label: "Ver boletos",
          onClick: () => { window.location.href = "/boletos"; },
        },
      });
    });
  };

  // Toast de notas reconciliadas — dispara evento `show-nota-toast` que é
  // capturado pelo `<NotaToastStack />` (montado em App.js). Isso bypassa
  // completamente o sonner p/ notes: JSX próprio, position fixed à ESQUERDA,
  // duração 5s, tamanho reduzido. Sonner continua sendo usado só para boletos.
  const disparaToastsNotas = (novasReconciliadas) => {
    const porStatus = novasReconciliadas.reduce((acc, n) => {
      const k = n.novo_status || "outros";
      (acc[k] = acc[k] || []).push(n);
      return acc;
    }, {});
    const rotaMaisComum = (arr) => {
      const nfse = arr.filter((n) => n.nota_tipo === "NFSE").length;
      return nfse * 2 >= arr.length ? "/nfse" : "/nfe";
    };
    Object.entries(porStatus).forEach(([status, notas]) => {
      const isBatch = notas.length > 1;
      const titulo = isBatch
        ? `${notas.length} notas ${status === "autorizada" ? "autorizadas"
                                    : status === "cancelada" ? "canceladas"
                                    : "rejeitadas"}`
        : (notas[0].mensagem || "Nota reconciliada");
      const desc = isBatch
        ? notas.slice(0, 3).map((n) => `${n.nota_tipo || "NFE"} #${n.nota_numero}`).join(" · ")
          + (notas.length > 3 ? ` +${notas.length - 3}` : "")
        : (status === "autorizada" ? "SEFAZ/Prefeitura confirmou"
          : status === "cancelada" ? "Cancelamento confirmado"
          : "Verifique nas Notas");
      const rota = isBatch ? rotaMaisComum(notas)
                           : (notas[0].nota_tipo === "NFSE" ? "/nfse" : "/nfe");
      try {
        window.dispatchEvent(new CustomEvent("show-nota-toast", {
          detail: {
            id: `${status}-${notas[0].id}`,
            status,
            titulo,
            desc,
            rota,
          }
        }));
      } catch {}
    });
  };

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 10000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    if (aberto) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [aberto]);

  const marcarLida = async (id) => {
    await api.put(`/notificacoes/${id}/lida`);
    carregar();
  };
  const marcarTodas = async () => {
    // Marca só as desta instância (uma por uma p/ não afetar o outro sino)
    await Promise.all(
      itens.filter((n) => !n.lida).map((n) => api.put(`/notificacoes/${n.id}/lida`))
    );
    carregar();
  };

  const fmtTempo = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const min = Math.floor((Date.now() - d) / 60000);
    if (min < 1) return "agora";
    if (min < 60) return `${min}m`;
    if (min < 1440) return `${Math.floor(min / 60)}h`;
    return `${Math.floor(min / 1440)}d`;
  };

  // ---- Visual: cores/ícones da instância ----
  // Sino de boletos: dinheiro/laranja | Sino de notas: fiscal/azul
  const badgeColor = isNota ? "bg-blue-600" : "bg-orange-500";
  const iconAcento = isNota ? "text-blue-600" : "text-orange-500";
  const painelTitulo = isNota ? "Notas Fiscais" : "Boletos & Pagamentos";
  const testidBtn = isNota ? "notif-bell-notas" : "notif-bell-boletos";
  const testidBadge = isNota ? "notif-badge-notas" : "notif-badge-boletos";
  const testidPanel = isNota ? "notif-panel-notas" : "notif-panel-boletos";
  const tooltip = isNota ? "Notificações de Notas Fiscais" : "Notificações de Boletos";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setAberto((v) => !v)}
        data-testid={testidBtn}
        className={`relative w-9 h-9 rounded-full grid place-items-center hover:bg-slate-100 transition ${naoLidas > 0 ? iconAcento : "text-slate-600"}`}
        title={tooltip}
      >
        {isNota
          ? <FileCheck2 className="w-5 h-5" />
          : <Receipt className="w-5 h-5" />}
        {naoLidas > 0 && (
          <span data-testid={testidBadge}
                className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full
                           ${badgeColor} text-white text-[10px] font-bold flex items-center justify-center`}>
            {naoLidas > 99 ? "99+" : naoLidas}
          </span>
        )}
      </button>

      {aberto && (
        <div className="absolute right-0 top-11 w-96 bg-white border border-slate-200 rounded-lg shadow-xl z-50"
             data-testid={testidPanel}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div>
              <div className="font-heading font-semibold text-slate-900 text-sm flex items-center gap-2">
                {isNota ? <FileCheck2 className={`w-4 h-4 ${iconAcento}`} /> : <Receipt className={`w-4 h-4 ${iconAcento}`} />}
                {painelTitulo}
              </div>
              <div className="text-xs text-slate-500">{naoLidas} não lida(s)</div>
            </div>
            <div className="flex items-center gap-1">
              {naoLidas > 0 && (
                <button onClick={marcarTodas} data-testid={`${testidBtn}-marcar-todas`}
                        className="text-xs text-blue-600 hover:text-blue-800 font-semibold">
                  Marcar todas
                </button>
              )}
              <button onClick={() => setAberto(false)}
                      className="w-7 h-7 grid place-items-center rounded hover:bg-slate-100">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
            {itens.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-400">
                <Bell className="w-6 h-6 mx-auto mb-2 opacity-40" />
                Nenhuma notificação
              </div>
            )}
            {itens.map((n) => {
              const ehNota = n.tipo === "nfe_reconciled";
              const notaOk = ehNota && n.novo_status === "autorizada";
              const notaErro = ehNota && n.novo_status !== "autorizada" && n.novo_status !== "cancelada";
              const notaCanc = ehNota && n.novo_status === "cancelada";
              return (
              <div key={n.id}
                   data-testid={`notif-item-${n.id}`}
                   className={`p-3 hover:bg-slate-50 flex items-start gap-3 ${!n.lida ? "bg-blue-50/40" : ""}`}>
                <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${
                  notaOk ? "bg-emerald-100"
                    : notaCanc ? "bg-amber-100"
                    : notaErro ? "bg-rose-100"
                    : n.tipo === "boleto_pago" ? "bg-blue-100"
                    : "bg-slate-100"
                }`}>
                  {ehNota
                    ? (notaErro
                        ? <AlertTriangle className="w-4 h-4 text-rose-600" />
                        : <FileCheck2 className={`w-4 h-4 ${notaOk ? "text-emerald-600" : "text-amber-600"}`} />)
                    : <CheckCircle2 className={`w-4 h-4 ${
                        n.tipo === "boleto_pago" ? "text-blue-600" : "text-slate-500"
                      }`} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${
                      ehNota ? (notaOk ? "bg-emerald-100 text-emerald-700"
                                 : notaCanc ? "bg-amber-100 text-amber-700"
                                 : "bg-rose-100 text-rose-700")
                      : n.provider === "asaas" ? "bg-sky-100 text-sky-700"
                      : n.provider === "inter" ? "bg-orange-100 text-orange-700"
                      : "bg-slate-100 text-slate-700"
                    }`}>{ehNota ? (n.nota_tipo || "NFE") : (n.provider || "banco")}</span>
                    <span className="text-[10px] text-slate-400">{fmtTempo(n.created_at)}</span>
                    {!n.lida && (
                      <span className="ml-auto w-2 h-2 rounded-full bg-blue-500" />
                    )}
                  </div>
                  <div className="text-sm text-slate-800 mt-1 font-medium truncate">
                    {ehNota
                      ? (n.mensagem || `Nota #${n.nota_numero} — ${n.cliente_nome || "Cliente"}`)
                      : (n.cliente_nome || "Cliente")}
                  </div>
                  <div className="text-xs text-slate-600 font-mono-fin">
                    {ehNota
                      ? `Status: ${n.novo_status || "?"}${n.valor ? ` • ${brl(n.valor)}` : ""}`
                      : `Recebido ${brl(n.valor || 0)}`}
                  </div>
                  {ehNota && n.motivo && (
                    <div className="text-[11px] text-rose-700 mt-1 whitespace-normal break-words" data-testid={`notif-motivo-${n.id}`}>
                      <strong>Motivo:</strong> {n.motivo}
                      {n.orientacao && <div className="text-slate-600 mt-0.5"><strong>Como resolver:</strong> {n.orientacao}</div>}
                    </div>
                  )}
                </div>
                {!n.lida && (
                  <button onClick={() => marcarLida(n.id)}
                          className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold shrink-0"
                          data-testid={`notif-lida-${n.id}`}>
                    Marcar
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
